// Custom implementation to replace tailwindcss/lib/util/color functions

/**
 * Parse color string to RGB values
 * @param {string} color - Color string (hex, rgb, rgba, hsl, named colors)
 * @returns {object|null} - Parsed color object with color array and alpha
 */
const parseColor = (color) => {
  if (!color) return null;

  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    let r, g, b;

    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      return null;
    }

    return {
      color: [r, g, b],
      alpha: 1
    };
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbMatch) {
    return {
      color: [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])],
      alpha: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1
    };
  }

  // Handle hsl/hsla colors
  const hslMatch = color.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%(?:,\s*([\d.]+))?\)/);
  if (hslMatch) {
    const h = parseInt(hslMatch[1]);
    const s = parseInt(hslMatch[2]) / 100;
    const l = parseInt(hslMatch[3]) / 100;
    const alpha = hslMatch[4] ? parseFloat(hslMatch[4]) : 1;

    // Convert HSL to RGB
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;

    let r, g, b;
    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }

    return {
      color: [
        Math.round((r + m) * 255),
        Math.round((g + m) * 255),
        Math.round((b + m) * 255)
      ],
      alpha
    };
  }

  // Handle named colors (basic set)
  const namedColors = {
    'black': [0, 0, 0],
    'white': [255, 255, 255],
    'red': [255, 0, 0],
    'green': [0, 128, 0],
    'blue': [0, 0, 255],
    'yellow': [255, 255, 0],
    'cyan': [0, 255, 255],
    'magenta': [255, 0, 255],
    'gray': [128, 128, 128],
    'grey': [128, 128, 128],
    'transparent': [0, 0, 0]
  };

  const lowerColor = color.toLowerCase();
  if (namedColors[lowerColor]) {
    return {
      color: namedColors[lowerColor],
      alpha: lowerColor === 'transparent' ? 0 : 1
    };
  }

  return null;
};

/**
 * Format color object to CSS color string
 * @param {object} colorObj - Color object with color array and alpha
 * @returns {string} - CSS color string
 */
const formatColor = (colorObj) => {
  if (!colorObj || !colorObj.color) return 'transparent';

  const [r, g, b] = colorObj.color;
  const alpha = colorObj.alpha !== undefined ? colorObj.alpha : 1;

  if (alpha === 1) {
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
};

export const createColor = (color, alpha) => {
  const parsedColor = parseColor(color);
  if (!parsedColor) return 'transparent';

  return formatColor({
    ...parsedColor,
    alpha,
  });
};

export const createColorRGB = (color) => {
  const parsedColor = parseColor(color);
  return parsedColor ? parsedColor.color.join(", ") : "255, 255, 255";
};

/***
 * https://awik.io/determine-color-bright-dark-using-javascript/
 */
export const checkIsDark = (color) => {
  // Variables for red, green, blue values
  let r, g, b, hsp;

  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If RGB --> store the red, green, blue values in separate variables
    color = color.match(/^rgb(?:a)*?\((\d+)(?:,|\s)*(\d+)(?:,|\s)*(\d+)/);

    r = color[1];
    g = color[2];
    b = color[3];
  } else {
    // If hex --> Convert it to RGB: http://gist.github.com/983661
    color = +("0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&"));

    r = color >> 16;
    g = (color >> 8) & 255;
    b = color & 255;
  }

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  // eslint-disable-next-line prefer-const
  hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 150) {
    //127.5
    return false; // "light";
  } else {
    return true; // "dark";
  }
};
